import { apiEndpoint } from "../utils/env";

/**
 * Document requests: somebody pastes what was asked for, and the archive answers line by line.
 *
 * <b>Everything that decides authority lives on the server.</b> The caller sends the ASK and the
 * purpose; the organisation, the acting user and the scope are resolved from the token. There is
 * deliberately no confirm and no deliver call here — a request stops at "proposed", and turning a
 * proposal into something that reaches a recipient is a human act the API does not offer yet.
 */
async function call(path, token, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (options.body) headers["Content-Type"] = "application/json";

  const res = await fetch(apiEndpoint(path), { ...options, headers });
  const txt = await res.text();
  let json = null;
  try { json = txt ? JSON.parse(txt) : null; } catch { json = txt; }

  if (!res.ok) {
    const msg = (json && json.message) || (typeof json === "string" ? json : res.statusText);
    const err = new Error(msg || `Request failed: ${res.status}`);
    err.status = res.status;
    err.code = json && json.code;
    err.details = json;
    throw err;
  }
  return json;
}

/** The caller's own requests, and the ones raised for them, newest first. */
export function listDocumentRequests(token, { take = 50 } = {}) {
  return call(`/api/DocumentRequests?take=${encodeURIComponent(take)}`, token);
}

/** One request with its revisions and the current revision's lines. */
export function getDocumentRequest(token, requestKey) {
  return call(`/api/DocumentRequests/${encodeURIComponent(requestKey)}`, token);
}

/**
 * Raises a request and answers it in one call.
 *
 * `purpose` is required by the server, not as a description but as the lawful-basis record every
 * access it causes is logged against.
 */
export function createDocumentRequest(token, { context, purpose, subjectKey, onBehalfOfUserName, title, sourceKind, sourceText, dbNames }) {
  return call("/api/DocumentRequests", token, {
    method: "POST",
    body: JSON.stringify({ context, purpose, subjectKey, onBehalfOfUserName, title, sourceKind, sourceText, dbNames }),
  });
}

/** Runs it again as a NEW revision. Nothing already recorded changes. */
export function rerunDocumentRequest(token, requestKey, { sourceText, dbNames } = {}) {
  return call(`/api/DocumentRequests/${encodeURIComponent(requestKey)}/rerun`, token, {
    method: "POST",
    body: JSON.stringify({ sourceText, dbNames }),
  });
}

/**
 * A person confirms which document goes for which line.
 *
 * Deliberately has no assistant equivalent: the server refuses a confirmation from a declared agent,
 * and that refusal is why an untrusted ask can be pointed at somebody's file at all.
 */
export function confirmDocumentRequest(token, requestKey, documents, consentObtained = false) {
  return call(`/api/DocumentRequests/${encodeURIComponent(requestKey)}/confirm`, token, {
    method: "POST",
    body: JSON.stringify({ documents, consentObtained }),
  });
}

/**
 * The recipients a delivery may go to.
 *
 * Names and references only — no addresses. What a person needs to pick correctly is enough to tell
 * two recipients apart; sending to the wrong one is the failure mode, not being unable to read an
 * email address.
 */
export function listDeliveryRecipients(token) {
  return call("/api/DocumentRequests/recipients", token);
}

/**
 * Sends a confirmed set.
 *
 * The recipient is an id from the list above. There is deliberately no way to pass an address: the
 * ask decides what to look for and never where anything goes.
 */
export function deliverDocumentRequest(token, requestKey, { recipientId, requireAccessCode }) {
  return call(`/api/DocumentRequests/${encodeURIComponent(requestKey)}/deliver`, token, {
    method: "POST",
    body: JSON.stringify({ recipientId, requireAccessCode }),
  });
}

export function cancelDocumentRequest(token, requestKey, reason) {
  return call(`/api/DocumentRequests/${encodeURIComponent(requestKey)}/cancel`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}
